import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PageFooterLayout from '../layouts/PageFooterLayout/PageFooterLayout';
import withAuthentication from 'apex-web/lib/hocs/withAuthentication';
import {
  BuySellContainer,
  BuySellMakeTransactionContainer
} from '../components/Retail/RetailBuySell';
import 'apex-web/lib/layouts/Responsive/EotcBuySellLayout/EotcBuySellDesktopLayout.css';
import BalancesListContainer from '../components/BalancesList/BalancesListContainer';
import MarketOverviewComponent from '../components/MarketOverview';
import InstrumentSelectorContainer from 'apex-web/lib/components/InstrumentSelector/InstrumentSelectorContainer';
import InstrumentTableContainer from '../components/InstrumentTable/InstrumentTableContainer';
import RecentActivityContainer from 'apex-web/lib/components/RecentActivity/RecentActivityContainer';
import TransferRequestNotificationList from 'apex-web/lib/components/TransferRequestsNotification/';
import { getBEMClasses } from '../helpers/cssClassesHelper';
import { useUiViewMode } from '../context/UiViewModeContext';
import DashboardViewToggle from '../components/DashboardViewToggle';
import DashboardQuickLinks from '../components/DashboardQuickLinks';
import DashboardPromoCarousel from '../components/DashboardPromoCarousel';
import './BuySellPage.css';
import './DashboardPage.css';
import DashboardSwapPairPanelContainer from '../components/DashboardSwapPairPanel/DashboardSwapPairPanelContainer';
import DashboardSnapshotCardsContainer from '../components/DashboardSnapshotCards/DashboardSnapshotCardsContainer';

const layoutClasses = getBEMClasses('dashboard-page');

class DashboardPageView extends React.Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  static propTypes = {
    viewMode: PropTypes.string.isRequired,
    setViewMode: PropTypes.func.isRequired,
    isPro: PropTypes.bool.isRequired,
    /** Stacked "balances below highlights" layout — same breakpoint as 1-col dashboard (≤1024) */
    basicUseMobileOrder: PropTypes.bool.isRequired
  };

  render() {
    const { t } = this.context;
    const { viewMode, setViewMode, isPro, basicUseMobileOrder } = this.props;

    return (
      <React.Fragment>
        <div className={layoutClasses('accent-bar')} />
        <div className={layoutClasses()}>
          <div className={layoutClasses('top-band', { basic: !isPro })}>
            <div className={layoutClasses('top-main-column')}>
              <div className={layoutClasses('view-mode-row')}>
                <DashboardViewToggle
                  value={viewMode}
                  onChange={setViewMode}
                  t={t}
                />
              </div>
              <div className={layoutClasses('top-cards-wrap')}>
                <DashboardSnapshotCardsContainer />
              </div>
              {isPro ? (
                <React.Fragment>
                  <div className={layoutClasses('market-overview-container')}>
                    <div className={layoutClasses('market-overview-container-left')}>
                      <div className={layoutClasses('market-overview-head')}>
                        <div className={layoutClasses('market-overview-title-row')}>
                          <div className={layoutClasses('market-header')}>
                            {t('Markets Overview')}
                          </div>
                          <div className="market-overview__instrument-selector">
                            <InstrumentSelectorContainer
                              customClass="market-overview-popup"
                              showFullNameInstrument
                              showTriangles
                            />
                          </div>
                        </div>
                        <div className={layoutClasses('market-overview-head-stats')}>
                          <InstrumentTableContainer customClass="market-overview-top-bar" />
                        </div>
                      </div>
                      <MarketOverviewComponent
                        omitPriceTable
                        hideInstrumentSelector
                      />
                    </div>
                  </div>
                  <div className={layoutClasses('recent-activity-container')}>
                    <RecentActivityContainer filterForSelected={false} />
                  </div>
                </React.Fragment>
              ) : basicUseMobileOrder ? (
                <React.Fragment>
                  <div className={layoutClasses('basic-highlights')}>
                    <DashboardPromoCarousel t={t} />
                  </div>
                  <div
                    className={
                      layoutClasses('basic-balances') +
                      ' ' +
                      layoutClasses('balances-panel', { basic: true })
                    }>
                    <BalancesListContainer isBasicView t={t} />
                  </div>
                  <div className={layoutClasses('basic-quick-links')}>
                    <DashboardQuickLinks t={t} />
                  </div>
                </React.Fragment>
              ) : (
                <div className={layoutClasses('basic-main')}>
                  <DashboardPromoCarousel t={t} />
                  <DashboardQuickLinks t={t} />
                </div>
              )}
            </div>
            <div className={layoutClasses('top-right-column')}>
              <div className={layoutClasses('top-swap-shell')}>
                <div className={layoutClasses('swap-card')}>
                  <BuySellMakeTransactionContainer>
                    <React.Fragment>
                      <DashboardSwapPairPanelContainer />
                      <BuySellContainer displayInstrumentSelector={false} />
                    </React.Fragment>
                  </BuySellMakeTransactionContainer>
                </div>
              </div>
              {isPro ? (
                <div className={layoutClasses('balances-panel', { basic: false })}>
                  <BalancesListContainer isBasicView={false} t={t} />
                </div>
              ) : !basicUseMobileOrder ? (
                <div
                  className={layoutClasses('balances-panel', { basic: true })}>
                  <BalancesListContainer isBasicView t={t} />
                </div>
              ) : null}
            </div>
          </div>
          {isPro ? (
            <div className={layoutClasses('notification-row')}>
              <TransferRequestNotificationList />
            </div>
          ) : null}
        </div>
        <div className={layoutClasses('footer')}>
          <PageFooterLayout />
        </div>
      </React.Fragment>
    );
  }
}

const useMaxWidth = maxPx => {
  const query = `(max-width: ${maxPx}px)`;
  const [matches, setMatches] = useState(
    () =>
      typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

const DashboardPage = props => {
  const { viewMode, setViewMode, isPro } = useUiViewMode();
  const basicUseMobileOrder = useMaxWidth(1024);
  return (
    <DashboardPageView
      {...props}
      viewMode={viewMode}
      setViewMode={setViewMode}
      isPro={isPro}
      basicUseMobileOrder={basicUseMobileOrder}
    />
  );
};

export default withAuthentication(DashboardPage);
